import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { CoverDropzone } from "../components/CoverDropzone";

const GENRES = ["Fantasy", "Thriller", "Romance", "Sci-Fi", "Non-Fiction", "Mystery", "Historical", "Young Adult", "Horror", "Literary Fiction"];

export default function CreateCampaign() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    pen_name: user?.display_name || "",
    synopsis: "",
    genre: "Fantasy",
    arc_format: "both",
    copies_available: 10,
    review_deadline: "",
    cover_url: "",
    requirements: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, copies_available: Number(form.copies_available) };
      const { data } = await api.post("/campaigns", payload);
      toast.success("Campaign published!");
      nav(`/campaigns/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const penOptions = [user?.display_name, ...(user?.pen_names || [])].filter(Boolean);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2">New campaign</div>
      <h1 className="font-serif text-4xl tracking-tight mb-8">Post an ARC campaign</h1>

      <form onSubmit={submit} className="paper-card p-6 space-y-5" data-testid="create-campaign-form">
        <div>
          <Label htmlFor="title">Book title</Label>
          <Input id="title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="create-title" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Pen name for this campaign</Label>
            <Select value={form.pen_name} onValueChange={(v) => set("pen_name", v)}>
              <SelectTrigger data-testid="create-penname"><SelectValue placeholder="Select pen name" /></SelectTrigger>
              <SelectContent>
                {penOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Add more pen names in your profile.</p>
          </div>
          <div>
            <Label>Genre</Label>
            <Select value={form.genre} onValueChange={(v) => set("genre", v)}>
              <SelectTrigger data-testid="create-genre"><SelectValue /></SelectTrigger>
              <SelectContent>{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="synopsis">Synopsis</Label>
          <Textarea id="synopsis" required rows={6} value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} data-testid="create-synopsis" />
        </div>

        <div>
          <Label>ARC format offered</Label>
          <RadioGroup value={form.arc_format} onValueChange={(v) => set("arc_format", v)} className="flex flex-wrap gap-4 mt-2" data-testid="create-format">
            {[
              { v: "ebook", label: "Ebook only" },
              { v: "physical", label: "Physical only" },
              { v: "both", label: "Ebook + Physical" },
            ].map((o) => (
              <div key={o.v} className="flex items-center gap-2">
                <RadioGroupItem value={o.v} id={`f-${o.v}`} data-testid={`create-format-${o.v}`} />
                <Label htmlFor={`f-${o.v}`}>{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="copies">Copies available</Label>
            <Input id="copies" type="number" min={1} max={10000} required value={form.copies_available} onChange={(e) => set("copies_available", e.target.value)} data-testid="create-copies" />
          </div>
          <div>
            <Label htmlFor="deadline">Review deadline</Label>
            <Input id="deadline" type="date" required value={form.review_deadline} onChange={(e) => set("review_deadline", e.target.value)} data-testid="create-deadline" />
          </div>
        </div>

        <div>
          <Label htmlFor="cover">Book cover</Label>
          <CoverDropzone value={form.cover_url} onChange={(v) => set("cover_url", v)} testId="cover-dropzone" />
          <div className="flex items-center gap-2 mt-2">
            <Input id="cover" type="url" placeholder="…or paste a cover URL" value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} data-testid="create-cover" />
          </div>
        </div>

        <div>
          <Label htmlFor="req">Reviewer requirements (optional)</Label>
          <Textarea id="req" rows={3} placeholder="Ex: Review on Goodreads &amp; Amazon within 30 days of receiving." value={form.requirements} onChange={(e) => set("requirements", e.target.value)} data-testid="create-requirements" />
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="ghost" onClick={() => nav("/author")} data-testid="create-cancel">Cancel</Button>
          <Button type="submit" disabled={loading} className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white" data-testid="create-submit">
            {loading ? "Publishing…" : "Publish campaign"}
          </Button>
        </div>
      </form>
    </div>
  );
}
